<template>
    <div class="container">
        <div class="row">
            <div class="col-md-12 text-end">
                <span style="font-size: 75%">
                    <a href="#" @click.prevent="useMyLocation">use my location <font-awesome-icon :icon="['fas','map-marker-alt']" /></a>
                </span>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <div class="input-group input-group-sm mb-3">
                    <span class="input-group-text">Search:</span>
                    <input v-model="searchString" type="text" class="form-control" aria-label="Search cities">
                </div>
                <div v-if="isSearching">
                    <select v-model="searchResult" class="form-select search-results" :size="searchSize" style="margin-top: 0px; margin-bottom: 20px;">
                        <option v-for="result in searchResults" :key="result.id" :value="result">{{ result.name }}, {{ result.region_name }} - {{ result.country_name }}</option>
                    </select>
                </div>
            </div>
        </div>

        <div v-if="visitedCities.length" class="row">
            <div class="col-md-12 text-center">
                <span style="font-size: 75%">Visited: </span>
                <span v-for="item in visitedCities" :key="item.slug">
                    <a href="#" @click.prevent="selectCity(item)" class="badge bg-light text-dark border m-1">{{ item.name }}</a>
                </span>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12 text-center">
                <span style="font-size: 75%">Suggestions: </span>
                <span v-for="item in filteredNearby" :key="item.id">
                    <a href="#" @click="selectCity(item)" class="badge bg-light text-dark border m-1">{{ item.name }}</a>
                </span>
                <span v-for="item in filteredTop10K" :key="item.id">
                    <a href="#" @click="selectCity(item)" class="badge bg-light text-dark border m-1">{{ item.name }}</a>
                </span>
                <span v-for="item in filteredTop1K" :key="item.id">
                    <a href="#" @click="selectCity(item)" class="badge bg-light text-dark border m-1">{{ item.name }}</a>
                </span>
            </div>
        </div>

        <div v-if="coastalCities.length" class="row">
            <div class="col-md-12 text-center">
                <span style="font-size: 75%">Coastal: </span>
                <span v-for="item in coastalCities" :key="item.id">
                    <a href="#" @click.prevent="selectCity(item)" class="badge bg-light text-dark border m-1">{{ item.name }}</a>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import locci from '@/api/locci'
import { setCookie, getCookie } from '@/utils/cookie'
import { geolocate } from '@/utils/geo'

const LOCATION_COOKIE = 'calendula_location'
const VISITED_COOKIE  = 'calendula_visited'
const MAX_VISITED     = 5

export default {
    name: 'Locator',
    data() {
        return {
            nearby: [],
            top1K: [],
            top10K: [],
            visitedCities: [],
            coastalCities: [],
            searchString: '',
            searchResults: [],
            searchResult: {},
            searchSize: 0,
            isSearching: false,
        }
    },
    mounted() {
        this.loadVisitedCities()
    },
    computed: {
        ...mapState(['city', 'isLocatorEnabled']),
        coastalIds() {
            return new Set(this.coastalCities.map(c => c.id))
        },
        filteredNearby() {
            return this.nearby.filter(c => !this.coastalIds.has(c.id))
        },
        filteredTop1K() {
            return this.top1K.filter(c => !this.coastalIds.has(c.id))
        },
        filteredTop10K() {
            return this.top10K.filter(c => !this.coastalIds.has(c.id))
        },
    },
    watch: {
        isLocatorEnabled(val) {
            if (val && this.city && this.city.slug) {
                locci.findNearby(this.city.slug).then(r => { this.nearby = r.data.results }).catch(() => {})
                locci.fetchTopCities(this.city.slug, 3).then(r => { this.top1K = r.data.results }).catch(() => {})
                locci.fetchTopCities(this.city.slug, 4).then(r => { this.top10K = r.data.results }).catch(() => {})
                locci.findNearbyCoastal(this.city.slug).then(r => { this.coastalCities = r.data.results }).catch(() => {})
            }
        },
        searchString(val) {
            if (!val) {
                this.isSearching = false
            } else {
                locci.searchCities(val).then(r => { this.searchResults = r.data.results })
            }
        },
        searchResult(city) {
            this.searchString = ''
            this.searchResults = []
            this.isSearching = false
            this.searchSize = 0
            this.selectCity(city)
        },
        searchResults(results) {
            const size = Math.min(results.length + 1, 8)
            this.searchSize = size
            this.isSearching = size > 1
        },
    },
    methods: {
        ...mapMutations(['setCity', 'setCityDatetime', 'setLocatorEnabled', 'setDate']),
        loadVisitedCities() {
            try {
                this.visitedCities = JSON.parse(getCookie(VISITED_COOKIE) || '[]')
            } catch {
                this.visitedCities = []
            }
        },
        saveVisitedCity(city) {
            const entry = { slug: city.slug, name: city.name, lat: city.lat, lon: city.lon, region_name: city.region_name, country_name: city.country_name, is_coastal: city.is_coastal }
            const list = [entry, ...this.visitedCities.filter(c => c.slug !== city.slug)].slice(0, MAX_VISITED)
            this.visitedCities = list
            setCookie(VISITED_COOKIE, JSON.stringify(list))
        },
        selectCity(city) {
            if (!city || !city.slug) return
            this.saveVisitedCity(city)
            this.setCity(city)
            this.setLocatorEnabled(false)
            this.nearby = []
            this.top1K = []
            this.top10K = []
            this.coastalCities = []
            setCookie(LOCATION_COOKIE, `${city.lat},${city.lon}`)
            locci.fetchCityDatetime(city.slug).then(r => {
                const cityDatetime = r.data.results
                this.setCityDatetime(cityDatetime)
                this.setDate(new Date(cityDatetime.current_year, cityDatetime.current_month - 1, cityDatetime.current_day))
                this.setCity({ ...city, timezone_slug: cityDatetime.timezone_slug })
            }).catch(() => {})
        },
        async useMyLocation() {
            try {
                const coords = await geolocate()
                const r = await locci.findNearest(coords.latitude, coords.longitude)
                this.selectCity(r.data.results)
            } catch {
                // user denied or geolocation unavailable
            }
        },
    },
}
</script>

<style scoped>
.search-results {
    font-size: 13px;
}
.search-results option {
    padding: 5px 8px;
}
</style>
